/* Copyright (c) 2020-present CloudIO Inc */

import { getIn, setIn } from '@cloudio-saas/mutx';
import { initQuickX, registerCustomFunction } from '@cloudio-saas/quickx';
import { Platform } from 'componentregistry/platform/Platform';
import { initFunctionDocs } from 'expression/builder/Functions';
import { getProfileValue, hasRole, isAdministrator } from 'root/useUser';
import { routeAtom } from 'routes/RouteState';
import tinyColor from 'tinycolor2';
import { ulid } from 'ulid';
import { hashCode } from 'utils/hash';
import { getUrl } from '../config/config';
import { translate } from '../utils/lang';
import { endOfWeek, startOfMonth } from 'date-fns';

const PALETTE = [
  '#5a7896',
  '#ff93af',
  '#ff4ba6',
  '#ff78ff',
  '#9f46e4',
  '#6457f9',
  '#0064fb',
  '#48dafd',
  '#00d4c8',
  '#19db7e',
  '#ace60f',
  '#ffd100',
  '#ffa800',
  '#ff7511',
  '#fb5779',
];

export const getColor = (value?: string | null, palette: string[] = PALETTE) =>
  palette[hashCode(value) % palette.length];

export default async function init(platform: Platform): Promise<void> {
  await initQuickX();
  registerCustomFunction({
    category: 'Number',
    name: 'roundTo',
    fn: (value: number, fractionDigits = 2): string => {
      const options = {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      };
      return new Intl.NumberFormat('en-US', options).format(value);
    },
    minParams: 1,
    maxParams: 2,
    description:
      'Returns the given number as string rounded to the given fractionDigits.',
    usage: 'roundTo(value, fractionDigits = 2)',
    example: 'roundTo(100.2345, 1)',
  });
  registerCustomFunction({
    category: 'Number',
    name: 'rad',
    fn: (value: number): number => {
      const degrees_to_radians = (rad: number) => (rad * 180.0) / Math.PI;

      return degrees_to_radians(value);
    },
    minParams: 1,
    maxParams: 1,
    description: 'Converts degrees to radians.',
    usage: 'rad(value)',
    example: 'Same as Math.rad',
  });
  registerCustomFunction({
    category: 'Number',
    name: 'deg',
    fn: (value: number): number => {
      const degreesToRads = (deg: number) => (deg * Math.PI) / 180.0;
      return degreesToRads(value);
    },
    minParams: 1,
    maxParams: 1,
    description:
      'Returns the given number as string rounded to the given fractionDigits.',
    usage: 'deg(value)',
    example: 'Same as Math.deg',
  });
  registerCustomFunction({
    category: 'Boolean',
    name: 'hasRole',
    fn: (...roles: string[]) =>
      !!roles.find((r) => hasRole(platform, platform.getter(routeAtom).app, r)),
    minParams: 1,
    maxParams: 20,
    description:
      'Returns true if atleast one of the passed role is assigned to the current user',
    usage: 'hasRole(role1, [role2, role2])',
    example: "hasRole('administrator')",
  });
  registerCustomFunction({
    category: 'Custom',
    name: 'getProfileValue',
    fn: (code: string) =>
      getProfileValue(platform, platform.getter(routeAtom).app, code),
    minParams: 1,
    maxParams: 1,
    description:
      'Returns the value configured for the given profile with `Push To Client` option checked.',
    usage: "getProfileValue('PROFILE_CODE')",
    example: "getProfileValue('HOME_PAGE')",
  });
  registerCustomFunction({
    category: 'Custom',
    name: 'appUid',
    fn: () => platform.getter(routeAtom).app,
    minParams: 0,
    maxParams: 0,
    description: 'Returns the appUid value.',
    usage: 'appUid()',
  });
  registerCustomFunction({
    category: 'Custom',
    name: 'setIn',
    fn: (obj, path, value) => {
      if (!Array.isArray(path)) {
        throw Error(
          `Path must be an array, instead found ${typeof path} ${path}`,
        );
      }
      const result = setIn(obj, path, value);
      return result;
    },
    minParams: 3,
    maxParams: 3,
    description: 'setIn',
    usage: "setIn({}, ['path'], 'some value')",
  });
  registerCustomFunction({
    category: 'Custom',
    name: 'getIn',
    fn: (obj, path, defaultValue) => getIn(obj, path, defaultValue),
    minParams: 3,
    maxParams: 3,
    description: 'getIn',
    usage: "getIn({}, ['path'], 'defaultValue')",
  });
  registerCustomFunction({
    category: 'Number',
    name: 'formatNumber',
    fn: (
      number: number,
      locale = 'en-US',
      options = {
        style: 'currency',
        currency: 'USD',
      },
    ) => new Intl.NumberFormat(locale, options).format(number),
    minParams: 1,
    maxParams: 3,
    description: 'formatNumber is a wrapper around Intl.NumberFormat',
    usage: 'formatNumber(value, locale, options)',
    example:
      "formatNumber(10000) or formatNumber(10000, 'en-US', {style: 'currency', currency: 'USD'})",
  });
  registerCustomFunction({
    category: 'Custom',
    name: 'hashCode',
    fn: (value?: string) => hashCode(value),
    minParams: 1,
    maxParams: 1,
    description: 'Returns a hash value of the given string.',
    usage: 'hashCode(param1)',
    example: "hashCode('Some Thing')",
  });
  registerCustomFunction({
    category: 'Custom',
    name: 'getColor',
    fn: getColor,
    minParams: 1,
    maxParams: 2,
    description:
      'Returns a color string calculated based on the hash value of the given string.',
    usage: 'getColor(stringValue, palette)',
    example:
      "getColor('Some Thing') or getColor('Some Thing', ['red', 'blue', 'orange'])",
  });
  registerCustomFunction({
    category: 'Custom',
    name: 'getReadableColor',
    fn: (color: string) =>
      color
        ? tinyColor.mostReadable(color, ['#ffffff', '#000000']).toHexString()
        : '#000',
    minParams: 1,
    maxParams: 1,
    description:
      'Returns either #000 or #fff depending on the given background color.',
    usage: 'getReadableColor(SOME_COLOR)',
    example: "getReadableColor('yellow') or getReadableColor('#00ff00')",
  });
  registerCustomFunction({
    category: 'Custom',
    name: 't',
    fn: translate,
    minParams: 1,
    maxParams: 1,
    description:
      "Returns a translated string based on the given primary language string and the current user's locale.",
    usage: 't(param1)',
    example: "t('Hi there!') or t('Submit')",
  });
  registerCustomFunction({
    category: 'other',
    name: 'ifNull',
    fn: (value: string, defaultValue: string) => {
      return value !== null && value !== undefined ? value : defaultValue;
    },
    minParams: 2,
    maxParams: 2,
    description:
      'Retuns param2 if param1 is null, else param1 will be returned.',
    usage: 'ifNull(param1, param2)',
    example: 'ifNull(param1, param2)',
  });
  registerCustomFunction({
    category: 'other',
    name: 'not',
    fn: (value: boolean) => {
      return value === true ? false : true;
    },
    minParams: 1,
    maxParams: 1,
    description: 'Retuns false if param1 is truthy else returns true',
    usage: 'not(param1)',
    example: 'not(param1)',
  });
  registerCustomFunction({
    category: 'other',
    name: 'nullVal',
    fn: () => {
      return null;
    },
    minParams: 0,
    maxParams: 0,
    description: 'Use this function to return a null value',
    usage: 'nullVal()',
    example: 'nullVal()',
  });
  registerCustomFunction({
    category: 'other',
    name: 'nvl',
    fn: (value: string, defaultValue: string) => {
      return value === null ? defaultValue : value;
    },
    minParams: 2,
    maxParams: 2,
    description:
      'Retuns param2 if param1 is null, else param1 will be returned.',
    usage: 'nvl(param1, param2)',
    example: 'nvl(param1, param2)',
  });

  registerCustomFunction({
    category: 'Custom',
    name: 'getInlineURLForAttachment',
    fn: (fileUid: string) => {
      return fileUid
        ? `${getUrl()}inline/file_attachment/${platform.appUid}/${fileUid}`
        : '';
    },
    minParams: 1,
    maxParams: 1,
    description:
      'Returns the inline url of the given file attachment uid. This is useful when you want to display an image as a background for Box.',
    usage: 'getInlineURLForAttachment($.ds.fileUid)',
    example: 'getInlineURLForAttachment($.ds.fileUid)',
  });

  registerCustomFunction({
    category: 'String',
    name: 'ulid',
    fn: () => ulid(),
    minParams: 0,
    maxParams: 0,
    description:
      'Returns a Universally Unique Lexicographically Sortable Identifier string. Refer to https://github.com/ulid/javascript for more details.',
    usage: 'ulid()',
    example: 'ulid()',
  });

  registerCustomFunction({
    category: 'String',
    name: 'constant',
    fn: () => {
      return 'ChangeMe!!';
    },
    minParams: 0,
    maxParams: 0,
    description: 'String constant values can be specified using single quotes',
    usage: 'ChangeMe!!',
    example: 'Yo, + #{name} + !',
  });

  registerCustomFunction({
    category: 'String',
    name: 'Space',
    fn: () => {
      return ' ';
    },
    minParams: 0,
    maxParams: 0,
    description: 'Blank space can be specified using single quotes',
    usage: ' ',
    example: ' ',
  });

  registerCustomFunction({
    category: 'String',
    name: 'Template Strings',
    fn: () => {
      return ' `param1 param2` ';
    },
    minParams: 0,
    maxParams: 0,
    description:
      'Template Strings use back-ticks (``) rather than the single quotes we are used to with regular strings.',
    usage: ' `param1 param2` ',
    example: 'Yo, + #{name} + !',
  });
  registerCustomFunction({
    category: 'String',
    name: 'chartAt',
    fn: (param: string, index: number) => {
      return param.charAt(index);
    },
    minParams: 2,
    maxParams: 2,
    description:
      'Returns a new string consisting of the character located at the specified offset into the given string.',
    usage: '#{ds.someAttr}.charAt(5)',
    example: 'param1.charAt(offset: number)',
  });
  registerCustomFunction({
    category: 'String',
    name: 'concat',
    fn: (...params: string[]) => {
      const _strings: string[] = [];
      return _strings.concat(params);
    },
    minParams: 1,
    maxParams: 20,
    description: 'Concatenates the string arguments and returns a new string.',
    usage: 'string1.concat(string2, string3, ...string20)',
    example: '#{ds.firstName}.concat(#{ds.lastName})',
  });
  registerCustomFunction({
    category: 'String',
    name: 'contains',
    fn: (param1: string, param2: string) => {
      return param1.includes(param2);
    },
    minParams: 2,
    maxParams: 2,
    description: 'Retuns true if param2 is found in param1 with matching case.',
    usage: 'contains(param1: string, param2: string)',
    example: 'contains(#{ds.attr}, US)',
  });
  registerCustomFunction({
    category: 'String',
    name: 'containsIgnoreCase',
    fn: (param1: string, param2: string) => {
      const p1 = param1.toLowerCase();
      const p2 = param2.toLowerCase();
      return p1.includes(p2);
    },
    minParams: 2,
    maxParams: 2,
    description:
      'Retuns true if param2 is found in param1 irrespective of the case.',
    usage: 'containsIgnoreCase(param1: string, param2: string)',
    example: 'containsIgnoreCase(#{ds.attr}, #{ds.attr})',
  });
  registerCustomFunction({
    category: 'String',
    name: 'endsWith',
    fn: (param1: string, param2: string) => {
      return param1.endsWith(param2);
    },
    minParams: 2,
    maxParams: 2,
    description: 'Retuns true if param1 ends with param2.',
    usage: 'endsWith(param1: string, param2: string)',
    example: 'endsWith(#{ds.attr}, #{ds.attr})',
  });
  registerCustomFunction({
    category: 'String',
    name: 'indexOf',
    fn: (param1: string, param2: string) => {
      return param1.indexOf(param2);
    },
    minParams: 2,
    maxParams: 2,
    description:
      'Returns the index within param1 of the first occurrence of the specified searchTerm. Returns -1 if the value is not found.',
    usage: 'indexOf(param1: string, searchTerm: string)',
    example: 'indexOf(#{ds.attr}, #{ds.attr})',
  });
  registerCustomFunction({
    category: 'String',
    name: 'lastIndexOf',
    fn: (param1: string, param2: string) => {
      return param1.lastIndexOf(param2);
    },
    minParams: 2,
    maxParams: 2,
    description:
      'Returns the index within param1 of the last occurrence of the specified searchTerm. Returns -1 if the value is not found.',
    usage: 'lastIndexOf(param1: string, searchTerm: string)',
    example: 'lastIndexOf(#{ds.attr}, #{ds.attr})',
  });
  registerCustomFunction({
    category: 'String',
    name: 'length',
    fn: (param1: string) => {
      return param1.length;
    },
    minParams: 2,
    maxParams: 2,
    description: 'Retuns the length of the given string.',
    usage: 'param1.length',
    example: '#{ds.attr}.length',
  });
  registerCustomFunction({
    category: 'String',
    name: 'match',
    fn: (param1: string, regex: string) => {
      return param1.match(regex);
    },
    minParams: 2,
    maxParams: 2,
    description: 'Retuns true if the given string matches the given regex.',
    usage: 'param1.match(regex: string)',
    example: '#{ds.attr}.match(regex)',
  });
  registerCustomFunction({
    category: 'String',
    name: 'replace',
    fn: (param1: string, target: string, replacement: string) => {
      return param1.replace(target, replacement);
    },
    minParams: 3,
    maxParams: 3,
    description:
      'Replaces each substring of the given string that matches the literal target sequence with the specified literal replacement sequence. The replacement proceeds from the beginning of the string to the end, for example, replacing "aa" with "b" in the string "aaa" will result in "ba" rather than "ab".',
    usage: 'replace(param1: string, target: string, replacement: string)',
    example: 'replace(#{ds.attr}, a, b)',
  });
  registerCustomFunction({
    category: 'String',
    name: 'replaceAll',
    fn: (param1: string, target: string, replacement: string) => {
      return param1.replaceAll(target, replacement);
    },
    minParams: 3,
    maxParams: 3,
    description:
      'Replaces each substring of the given string that matches the given regular expression with the given replacement.',
    usage: 'replaceAll(param1: string, regex: string, replacement: string)',
    example: 'replaceAll(#{ds.attr}, d, a)',
  });
  registerCustomFunction({
    category: 'String',
    name: 'startsWith',
    fn: (param1: string, param2: string) => {
      return param1.startsWith(param2);
    },
    minParams: 2,
    maxParams: 2,
    description:
      'Returns true if the given string starts with the specified prefix.',
    usage: 'startsWith(param1: string, prefix: string)',
    example: 'startsWith(#{ds.attr}, a)',
  });
  registerCustomFunction({
    category: 'String',
    name: 'substr',
    fn: (param1: string, beginIndex: number, endIndex: number) => {
      return param1.substring(beginIndex, endIndex);
    },
    minParams: 3,
    maxParams: 3,
    description:
      'Returns a string that is a substring of the given string. The substring begins at the specified beginIndex and extends to the character at index endIndex - 1. If endIndex is omitted then it extends to the end of the string.',
    usage: 'substr(param1: string, beginIndex: number, ?endIndex: number)',
    example: 'substr(#{ds.attr}, 2, 5)',
  });
  registerCustomFunction({
    category: 'String',
    name: 'toLowerCase',
    fn: (param1: string) => {
      return param1.toLowerCase();
    },
    minParams: 1,
    maxParams: 1,
    description: 'Same as String.toLowerCase.',
    usage: 'toLowerCase(param1: string)',
    example: 'toLowerCase(#{ds.attr})',
  });
  registerCustomFunction({
    category: 'String',
    name: 'toUpperCase',
    fn: (param1: string) => {
      return param1.toUpperCase();
    },
    minParams: 1,
    maxParams: 1,
    description: 'Same as String.toUpperCase.',
    usage: 'toUpperCase(param1: string)',
    example: 'toUpperCase(#{ds.attr})',
  });
  registerCustomFunction({
    category: 'String',
    name: 'trim',
    fn: (param1: string) => {
      return param1.trim();
    },
    minParams: 1,
    maxParams: 1,
    description:
      'Returns a new string with any leading and trailing whitespace of the given string removed.',
    usage: 'trim(param1: string)',
    example: 'trim(#{ds.attr})',
  });
  registerCustomFunction({
    category: 'Custom',
    name: 'isSuperAdmin',
    fn: () => isAdministrator(platform, 'cloudio'),
    minParams: 0,
    maxParams: 0,
    description:
      'Returns true if the logged in user has admin role in cloudIO application.',
    usage: 'isSuperAdmin()',
    example: 'isSuperAdmin()',
  });

  registerCustomFunction({
    category: 'Date',
    name: 'startOfMonth',
    fn: (date) => startOfMonth(new Date(date)).toISOString(),
    minParams: 1,
    maxParams: 1,
    description:
      'returns the date of start day of the month. You can chain it with addDays to get other dates.',
    usage: 'startOfMonth(param1)',
    example: 'startOfMonth(sysdate())',
  });

  registerCustomFunction({
    category: 'Date',
    name: 'endOfWeek',
    fn: (date) => endOfWeek(new Date(date)).toISOString(),
    minParams: 1,
    maxParams: 1,
    description:
      'returns the date of end day of the month. You can chain it with addDays to get other dates.',
    usage: 'endOfWeek(param1)',
    example: 'endOfWeek(sysdate())',
  });

  initFunctionDocs();
}
